import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import Paper from "@mui/material/Paper";
import { ForecastResult } from "../../utilities/forecast";
import { numberWithCommas } from "../Oversigt";
import { danishMonths } from "../../utilities/utils";

// ------------------------------------------------------------------
// ForecastChart — single-metric time series with forecast.
//
// Default view (simple):
//   - Solid blue line: history
//   - Dashed blue line: forecast (ensemble of HW + SN)
//   - Shaded band: 80% prediction interval
//   - Vertical dotted line: "I dag"
//
// Advanced view (showMethods=true):
//   - Also draws HW line and SN line so power users can see method
//     disagreement
//   - Also draws 95% band
//
// Matches Statistik aesthetic: #1976d2 primary, 20px card padding,
// tabular-nums on all numeric output, plain Danish labels.
// ------------------------------------------------------------------

interface ForecastChartProps {
    title: string;
    unit: "kr" | "stk";
    forecast: ForecastResult;
    showMethods: boolean;
}

interface ChartRow {
    key: string;
    label: string;
    actual?: number;
    mean?: number;
    hw?: number;
    sn?: number;
    band80?: [number, number];
    band95?: [number, number];
    isForecast: boolean;
}

function monthLabel(year: number, monthIndex: number): string {
    const m = danishMonths[monthIndex] ?? String(monthIndex + 1);
    return `${m.charAt(0).toUpperCase()}${m.slice(1, 3)} '${String(year).slice(2)}`;
}

function formatKr(n: number): string {
    return `${numberWithCommas(Math.round(n))} kr`;
}

function formatCount(n: number): string {
    return `${numberWithCommas(Math.round(n))}`;
}

const HISTORY_COLOR = "#1976d2";
const FORECAST_COLOR = "#1976d2";        // Same blue — dashed distinguishes it.
const HW_COLOR = "#7b1fa2";
const SN_COLOR = "#ef6c00";
const BAND80_COLOR = "rgba(25, 118, 210, 0.18)";
const BAND95_COLOR = "rgba(25, 118, 210, 0.08)";

export function ForecastChart({ title, unit, forecast, showMethods }: ForecastChartProps) {
    const fmt = unit === "kr" ? formatKr : formatCount;

    const rows: ChartRow[] = [];

    for (const p of forecast.history) {
        rows.push({
            key: p.key,
            label: monthLabel(p.year, p.monthIndex),
            actual: p.value,
            isForecast: false,
        });
    }

    const lastHist = forecast.history[forecast.history.length - 1];

    // Bridge: repeat the last history value as the starting point of the forecast
    // line so the dashed line visually connects to the actual trace.
    if (lastHist && forecast.ensemble.length > 0) {
        const lastRow = rows[rows.length - 1];
        lastRow.mean = lastHist.value;
        lastRow.hw = lastHist.value;
        lastRow.sn = lastHist.value;
        lastRow.band80 = [lastHist.value, lastHist.value];
        lastRow.band95 = [lastHist.value, lastHist.value];
    }

    // Clamp lower bounds to 0. Revenue and registration counts cannot be
    // negative, so a band dipping below zero is physically meaningless and
    // misleading. The upper bound is not clamped.
    for (let i = 0; i < forecast.ensemble.length; i++) {
        const e = forecast.ensemble[i];
        const h = forecast.hw?.[i];
        const s = forecast.sn?.[i];
        rows.push({
            key: e.key,
            label: monthLabel(e.year, e.monthIndex),
            mean: e.mean,
            hw: h?.mean,
            sn: s?.mean,
            band80: [Math.max(0, e.lower80), e.upper80],
            band95: [Math.max(0, e.lower95), e.upper95],
            isForecast: true,
        });
    }

    return (
        <Paper elevation={2} style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16, color: "#1a1a1a" }}>{title}</h3>
                {forecast.trainedThrough && (
                    <span style={{ fontSize: 12, color: "#8a8f96" }}>
                        Trænet til og med {forecast.trainedThrough}
                    </span>
                )}
            </div>

            {forecast.insufficientReason ? (
                <p style={{ fontSize: 13, color: "#888", marginTop: 12 }}>{forecast.insufficientReason}</p>
            ) : (
                <>
                    <ResponsiveContainer width="100%" height={320}>
                        <ComposedChart data={rows} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={2} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => unit === "kr" ? `${numberWithCommas(v)}` : `${v}`} />
                            <Tooltip content={({ active, payload }: any) => {
                                if (!active || !payload || !payload.length) return null;
                                const row: ChartRow = payload[0].payload;
                                return (
                                    <div style={{ background: "white", border: "1px solid #ccc", padding: 10, borderRadius: 4, minWidth: 200 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 6, color: "#1a1a1a" }}>{row.label}</div>
                                        {row.actual !== undefined && (
                                            <div style={{ fontSize: 13, color: HISTORY_COLOR, fontWeight: 600 }}>
                                                Faktisk: {fmt(row.actual)}
                                            </div>
                                        )}
                                        {row.isForecast && row.mean !== undefined && (
                                            <>
                                                <div style={{ fontSize: 13, color: FORECAST_COLOR, fontWeight: 600 }}>
                                                    Forventet: {fmt(row.mean)}
                                                </div>
                                                {row.band80 && (
                                                    <div style={{ fontSize: 12, color: "#5f6368", marginTop: 2 }}>
                                                        Spænd: {fmt(row.band80[0])} – {fmt(row.band80[1])}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 11, color: "#8a8f96", marginTop: 4 }}>
                                                    80% sandsynlighed for at lande i dette spænd
                                                </div>
                                                {showMethods && row.hw !== undefined && (
                                                    <div style={{ fontSize: 11, color: HW_COLOR, marginTop: 4 }}>
                                                        Holt-Winters: {fmt(row.hw)}
                                                    </div>
                                                )}
                                                {showMethods && row.sn !== undefined && (
                                                    <div style={{ fontSize: 11, color: SN_COLOR }}>
                                                        Sæsonal-naiv: {fmt(row.sn)}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            }} />

                            {/* 95% band only visible in advanced mode. */}
                            {showMethods && (
                                <Area
                                    type="monotone"
                                    dataKey="band95"
                                    fill={BAND95_COLOR}
                                    stroke="none"
                                    isAnimationActive={false}
                                />
                            )}
                            {/* 80% band — always visible when forecast exists. */}
                            <Area
                                type="monotone"
                                dataKey="band80"
                                fill={BAND80_COLOR}
                                stroke="none"
                                isAnimationActive={false}
                            />

                            {/* History line */}
                            <Line
                                type="monotone"
                                dataKey="actual"
                                stroke={HISTORY_COLOR}
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                            />

                            {/* Forecast line (ensemble) */}
                            <Line
                                type="monotone"
                                dataKey="mean"
                                stroke={FORECAST_COLOR}
                                strokeWidth={2}
                                strokeDasharray="6 4"
                                dot={false}
                                isAnimationActive={false}
                            />

                            {/* Advanced method lines */}
                            {showMethods && (
                                <Line
                                    type="monotone"
                                    dataKey="hw"
                                    stroke={HW_COLOR}
                                    strokeWidth={1.25}
                                    strokeDasharray="3 3"
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            )}
                            {showMethods && (
                                <Line
                                    type="monotone"
                                    dataKey="sn"
                                    stroke={SN_COLOR}
                                    strokeWidth={1.25}
                                    strokeDasharray="3 3"
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            )}

                            {lastHist && (
                                <ReferenceLine
                                    x={monthLabel(lastHist.year, lastHist.monthIndex)}
                                    stroke="#8a8f96"
                                    strokeDasharray="2 4"
                                    label={{ value: "I dag", position: "top", fontSize: 11, fill: "#5f6368" }}
                                />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>

                    {/* Plain-Danish legend row below the chart. */}
                    <div style={{ display: "flex", gap: 20, marginTop: 8, fontSize: 12, color: "#5f6368", flexWrap: "wrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ display: "inline-block", width: 20, height: 2, background: HISTORY_COLOR }} />
                            Faktisk (sidste 3 år)
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ display: "inline-block", width: 20, height: 0, borderTop: `2px dashed ${FORECAST_COLOR}` }} />
                            Forventet (næste 12 mdr)
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ display: "inline-block", width: 20, height: 10, background: BAND80_COLOR }} />
                            Usikkerhed (80% interval)
                        </span>
                        {showMethods && (
                            <>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ display: "inline-block", width: 20, height: 0, borderTop: `1px dashed ${HW_COLOR}` }} />
                                    Holt-Winters
                                </span>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ display: "inline-block", width: 20, height: 0, borderTop: `1px dashed ${SN_COLOR}` }} />
                                    Sæsonal-naiv
                                </span>
                            </>
                        )}
                    </div>
                </>
            )}
        </Paper>
    );
}
